<template>
  <v-dialog
    :value="value"
    max-width="800"
    persistent
    :retain-focus="false"
    @input="$emit('input', $event)"
  >
    <v-card>
      <v-card-title>
        領収書を分割発行
        <v-spacer />
        <v-btn
          icon
          :ripple="false"
          @click="$emit('input', false)"
        >
          <v-icon>{{ icons.mdiClose }}</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider />

      <v-card-text class="pa-4">
        <div class="text-caption mb-2">
          伝票合計 <strong>¥{{ realTotal.toLocaleString() }}</strong>
        </div>

        <v-row dense>
          <v-col
            cols="12"
            md="7"
          >
            <div class="d-flex align-center mb-2">
              <span class="text-body-2 font-weight-bold">宛先 {{ localRows.length }} 名</span>
              <v-spacer />
              <v-btn
                small
                text
                :ripple="false"
                @click="addRow"
              >
                <v-icon
                  left
                  small
                >
                  {{ icons.mdiPlus }}
                </v-icon>
                宛先を追加
              </v-btn>
            </div>

            <v-card
              v-for="(row, idx) in localRows"
              :key="idx"
              outlined
              :elevation="idx === focusedIndex ? 2 : 0"
              :class="['mb-2', idx === focusedIndex ? 'split-row--focused' : '']"
              @click="focusedIndex = idx"
            >
              <v-card-text class="pa-3">
                <div class="d-flex align-center">
                  <v-chip
                    x-small
                    label
                    class="mr-2"
                  >
                    #{{ idx + 1 }}
                  </v-chip>
                  <v-text-field
                    v-model="row.to"
                    placeholder="宛名を入力（任意）"
                    dense
                    hide-details
                    flat
                    solo
                    background-color="transparent"
                  >
                    <template #append>
                      様
                    </template>
                  </v-text-field>
                  <v-btn
                    icon
                    x-small
                    :ripple="false"
                    :disabled="localRows.length <= 1"
                    @click.stop="removeRow(idx)"
                  >
                    <v-icon small>
                      {{ icons.mdiTrashCanOutline }}
                    </v-icon>
                  </v-btn>
                </div>
                <div class="d-flex align-center mt-1">
                  <span class="text-caption mr-2">¥</span>
                  <v-text-field
                    :value="Number(row.amount).toLocaleString()"
                    readonly
                    dense
                    hide-details
                    flat
                    solo
                    background-color="transparent"
                    class="text-right"
                  />
                </div>
                <div class="d-flex align-center mt-2">
                  <v-combobox
                    :value="row.note"
                    :items="notePresetTexts"
                    :search-input="row.note"
                    label="但し書き"
                    no-data-text="選択可能なデータがありません"
                    dense
                    hide-details
                    outlined
                    @input="row.note = $event"
                    @update:search-input="row.note = $event"
                  />
                </div>
              </v-card-text>
            </v-card>

            <v-divider class="my-2" />
            <div class="d-flex align-center text-caption grey--text">
              <span>小計</span>
              <v-spacer />
              <span>¥{{ subtotal.toLocaleString() }}</span>
            </div>
            <div class="d-flex align-center text-caption grey--text">
              <span>税サ等</span>
              <v-spacer />
              <span>¥{{ serviceFee.toLocaleString() }}</span>
            </div>
            <div
              v-if="processingFee > 0"
              class="d-flex align-center text-caption grey--text"
            >
              <span>会計手数料</span>
              <v-spacer />
              <span>¥{{ processingFee.toLocaleString() }}</span>
            </div>
            <div
              v-if="discountFee > 0"
              class="d-flex align-center text-caption grey--text"
            >
              <span>値引き</span>
              <v-spacer />
              <span>-¥{{ discountFee.toLocaleString() }}</span>
            </div>
            <div class="d-flex align-center text-caption mt-1">
              <span>伝票合計</span>
              <v-spacer />
              <span>¥{{ realTotal.toLocaleString() }}</span>
            </div>
            <div class="d-flex align-center text-caption">
              <span>配分済</span>
              <v-spacer />
              <span>¥{{ allocatedTotal.toLocaleString() }}</span>
            </div>
            <div
              class="d-flex align-center text-caption"
              :class="{ 'red--text': remaining < 0, 'orange--text': remaining > 0, 'success--text': remaining === 0 }"
            >
              <span>残額</span>
              <v-spacer />
              <span>¥{{ remaining.toLocaleString() }}</span>
            </div>
          </v-col>

          <v-col
            cols="12"
            md="5"
          >
            <div class="text-caption mb-1">
              金額を入力 · 宛先 #{{ focusedIndex + 1 }}
            </div>
            <v-card
              outlined
              class="pa-3"
            >
              <div class="text-center text-h5 mb-2">
                ¥{{ Number(localRows[focusedIndex] && localRows[focusedIndex].amount || 0).toLocaleString() }}
              </div>
              <v-btn
                block
                color="primary"
                :ripple="false"
                :disabled="remaining === 0"
                class="mb-2"
                @click="assignRemainingToFocused"
              >
                残額 ¥{{ Math.max(remaining, 0).toLocaleString() }} を割当
              </v-btn>

              <v-row dense>
                <v-col
                  v-for="key in ['7','8','9','4','5','6','1','2','3','00','0','BS']"
                  :key="key"
                  cols="4"
                >
                  <v-btn
                    block
                    :ripple="false"
                    @click="pressKey(key)"
                  >
                    <span v-if="key === 'BS'">
                      <v-icon>{{ icons.mdiBackspace }}</v-icon>
                    </span>
                    <span v-else>{{ key }}</span>
                  </v-btn>
                </v-col>
                <v-col cols="6">
                  <v-btn
                    block
                    :ripple="false"
                    @click="clearFocused"
                  >
                    C
                  </v-btn>
                </v-col>
                <v-col cols="6">
                  <v-btn
                    block
                    color="primary"
                    :ripple="false"
                    :disabled="focusedIndex >= localRows.length - 1"
                    @click="focusNext"
                  >
                    次の宛先へ
                  </v-btn>
                </v-col>
              </v-row>
            </v-card>

            <div
              v-if="slipBaseOptions"
              class="mt-3"
            >
              <div class="text-caption mb-1">
                プレビュー · 宛先 #{{ focusedIndex + 1 }}
              </div>
              <Preview :slip-svg="rowSlipSvg" />
            </div>
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider />
      <v-card-actions class="pa-3">
        <span
          v-if="isValid"
          class="text-caption success--text"
        >
          ✓ 配分が一致しています
        </span>
        <span
          v-else
          class="text-caption red--text"
        >
          残額を 0 にしてください
        </span>
        <v-spacer />
        <v-btn
          text
          :ripple="false"
          @click="$emit('input', false)"
        >
          キャンセル
        </v-btn>
        <v-btn
          color="primary"
          :ripple="false"
          :disabled="!isValid"
          @click="confirm"
        >
          確定して閉じる
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { ref, computed, watch } from '@vue/composition-api'
import {
  mdiClose,
  mdiPlus,
  mdiTrashCanOutline,
  mdiBackspace,
} from '@mdi/js'
import { useReceiptSlipPrint } from '@yonarezi/yonarezi3.slip-print'
import { remainingAmount, isValidSplit, assignRemaining } from '@/views/composable/useReceiptSplit'
import Preview from '@/views/components/printer/Preview.vue'

export default {
  components: { Preview },
  props: {
    value: { type: Boolean, default: false },
    rows: { type: Array, default: () => [] },
    realTotal: { type: Number, required: true },
    subtotal: { type: Number, default: 0 },
    serviceFee: { type: Number, default: 0 },
    processingFee: { type: Number, default: 0 },
    discountFee: { type: Number, default: 0 },
    receiptNotePresets: { type: Array, default: () => [] },
    slipBaseOptions: { type: [Object, null], default: null },
    initialFocusIndex: { type: Number, default: 0 },
  },
  setup(props, { emit }) {
    const localRows = ref(props.rows.map(r => ({ ...r })))
    const focusedIndex = ref(props.initialFocusIndex)

    watch(() => props.value, open => {
      if (open) {
        localRows.value = props.rows.map(r => ({ ...r }))
        focusedIndex.value = props.initialFocusIndex
      }
    })

    // 但し書きはプリセットも自由入力も許容したいので、items は文字列配列に正規化する
    // (v-combobox は v-select と違って自由入力された文字列もそのまま v-model に流れる)
    const notePresetTexts = computed(() => (props.receiptNotePresets || []).map(p => p?.attributes?.content).filter(Boolean))

    // フォーカス中の行用に領収書SVGプレビューを生成する。
    // 既存の useReceiptSlipPrint を行データで呼び出すと、行ごとの宛名・金額・但し書きを反映したSVGが得られる。
    const focusedRowOptions = computed(() => ({
      ...(props.slipBaseOptions || {}),
      customerName: localRows.value[focusedIndex.value]?.to || '',
      receiptNote: localRows.value[focusedIndex.value]?.note || '',
      date: localRows.value[focusedIndex.value]?.issuedDate || new Date(),
      total: Number(localRows.value[focusedIndex.value]?.amount || 0),
      receiptSlipNumber: '',
      footerItems: [],
    }))
    const { svg: rowSlipSvg } = useReceiptSlipPrint(focusedRowOptions)

    const allocatedTotal = computed(() => localRows.value.reduce((acc, r) => acc + Number(r.amount || 0), 0))
    const remaining = computed(() => remainingAmount(props.realTotal, localRows.value.map(r => Number(r.amount || 0))))
    const isValid = computed(() => isValidSplit(props.realTotal, localRows.value.map(r => Number(r.amount || 0))))

    const pressKey = key => {
      const row = localRows.value[focusedIndex.value]
      if (!row) return
      const current = String(Number(row.amount || 0))
      let next
      if (key === 'BS') {
        next = current.length <= 1 ? '0' : current.slice(0, -1)
      } else {
        const appended = current === '0' ? key : current + key
        next = appended
      }
      row.amount = Number(next)
    }

    const clearFocused = () => {
      const row = localRows.value[focusedIndex.value]
      if (row) row.amount = 0
    }

    const assignRemainingToFocused = () => {
      const amounts = localRows.value.map(r => Number(r.amount || 0))
      const next = assignRemaining(amounts, focusedIndex.value, props.realTotal)
      localRows.value = localRows.value.map((r, i) => ({ ...r, amount: next[i] }))
    }

    const focusNext = () => {
      if (focusedIndex.value < localRows.value.length - 1) {
        focusedIndex.value += 1
      }
    }

    const addRow = () => {
      const preset = props.receiptNotePresets[0]
      localRows.value.push({
        to: '',
        amount: 0,
        note: preset?.attributes?.content || '',
        issuedDate: new Date(),
      })
      focusedIndex.value = localRows.value.length - 1
    }

    const removeRow = idx => {
      if (localRows.value.length <= 1) return
      localRows.value.splice(idx, 1)
      if (focusedIndex.value >= localRows.value.length) {
        focusedIndex.value = localRows.value.length - 1
      }
    }

    const confirm = () => {
      emit('confirm', localRows.value.map(r => ({ ...r })))
    }

    return {
      localRows,
      focusedIndex,
      notePresetTexts,
      rowSlipSvg,
      allocatedTotal,
      remaining,
      isValid,
      pressKey,
      clearFocused,
      assignRemainingToFocused,
      focusNext,
      addRow,
      removeRow,
      confirm,
      icons: {
        mdiClose, mdiPlus, mdiTrashCanOutline, mdiBackspace,
      },
    }
  },
}
</script>

<style scoped>
.split-row--focused {
  border-color: var(--v-primary-base) !important;
  border-width: 2px;
}
</style>
